import { olympiadPerformanceRecords, summarizeOlympiadPerformance, olympiadPlayerPerformance, olympiadLiveMatchScore } from "../helpers/chessOlympiadPerformance";
import { olympiadRosterStatistics, placementBins } from "../helpers/chessOlympiadStatistics";
import { chessPlayerBirthLabel } from "../helpers/chessPlayerBirth";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { BETTY_MODEL_STRATEGY, createTournamentStrengthReference, initializeTournamentPlayer, MISSING_ELO_INITIALIZATION_LABEL } from "../helpers/chessBettyStrength";
import { CHESS_MODEL_STRATEGY, DEFAULT_CHESS_MODEL_PARAMETERS } from "../helpers/chessProbability";
import { OLYMPIAD_MODEL_MINUTES, boardScoresFromResult, boardResultInputValue, boardResultLabel, whiteResultFromTeamAScore, boardScoreLabel } from "../helpers/chessOlympiadResults";
import {
  buildBoardProbabilities,
  calculateTeamMatchProbabilities,
} from "../helpers/chessTeamMatch";
import {
  initialOlympiadRanking,
  olympiadStandings,
} from "../helpers/chessOlympiadPairing";
import {
  simulateOlympiadTournament,
} from "../helpers/chessOlympiadSimulation";
import {
  loadChessOlympiadField,
  listChessOlympiadMatches,
  saveChessOlympiadMatch,
  previewChessOlympiadResults,
  applyChessOlympiadResults,
} from "../helpers/chessPersistence";
import "./ChessOlympiadWorkspace.css";

const ROUNDS = 11;
const pct = (value) => `${(100 * Number(value || 0)).toFixed(1)} %`;
export const formatProbabilityTriplet = (values) => {
  const raw = values.map(Number);
  if (raw.length !== 3 || raw.some((value) => !Number.isFinite(value) || value < 0 || value > 1)
      || Math.abs(raw.reduce((sum, value) => sum + value, 0) - 1) > 1e-12) {
    throw new Error("Invalid chess probability distribution.");
  }
  // Apportion display rounding only; never repair or normalize model output.
  const exactBasisPoints = raw.map((value) => value * 10000);
  const basisPoints = exactBasisPoints.map(Math.floor);
  let remainder = 10000 - basisPoints.reduce((sum, value) => sum + value, 0);
  [...exactBasisPoints.keys()]
    .sort(
      (a, b) =>
        exactBasisPoints[b] - basisPoints[b] -
        (exactBasisPoints[a] - basisPoints[a])
    )
    .forEach((index) => {
      if (remainder > 0) {
        basisPoints[index] += 1;
        remainder -= 1;
      }
    });
  return basisPoints.map((value, index) => {
    if (raw[index] > 0 && value === 0) return "<0,01 %";
    if (raw[index] < 1 && value === 10000) return ">99,99 %";
    return `${(value / 100).toFixed(2).replace(".", ",")} %`;
  });
};
const pct0 = (value) => formatProbabilityTriplet([value, 1 - value, 0])[0];
const odd = (value) =>
  value !== null && value !== undefined && Number.isFinite(Number(value)) && Number(value) >= 1
    ? Number(value).toFixed(2).replace(".", ",")
    : "–";
const asPlayer = (entry, fallbackId) => ({
  id: entry?.player_id || fallbackId,
  elo: entry?.missing ? undefined : Number(entry?.rating_used),
  drawFactor: Number(entry?.individual_draw_factor ?? 1),
});
export const olympiadRosterPlayerLabel = (entry) =>
  entry ? `${entry.name_used} · ${entry.rating_used} Elo` : "–";
const placeholderPlayer = (team, board) => ({
  player_id: `${team?.id || "unknown"}-missing-${board}`,
  board_order: board,
  name_used: "Nicht gemeldet",
  rating_used: null,
  title_used: "",
  missing: true,
});
const defaultLineup = (team) =>
  Array.from({ length: 4 }, (_, index) =>
    (team?.roster || [])[index] || placeholderPlayer(team, index + 1)
  );
export const olympiadTeamDisplayName = (team) => {
  const name = String(team?.name || "").trim();
  return /^USAUnited States of America$/i.test(name) ? "USA" : name;
};
export function selectOlympiadLineup(current, entry) {
  const exists = current.some((item) => item.player_id === entry.player_id);
  if (!exists && current.length >= 4) return current;
  return (
    exists
      ? current.filter((item) => item.player_id !== entry.player_id)
      : [...current, entry]
  ).sort((a, b) => Number(a.board_order) - Number(b.board_order));
}
const FLAGS = {
  JAM: "🇯🇲",
  GER: "🇩🇪",
  IND: "🇮🇳",
  GEO: "🇬🇪",
  SRB: "🇷🇸",
  USA: "🇺🇸",
  CHN: "🇨🇳",
  UKR: "🇺🇦",
  UZB: "🇺🇿",
  ARM: "🇦🇲",
  AZE: "🇦🇿",
  FRA: "🇫🇷",
  ESP: "🇪🇸",
  ENG: "🇬🇧",
  NED: "🇳🇱",
  NOR: "🇳🇴",
  POL: "🇵🇱",
  HUN: "🇭🇺",
  ROU: "🇷🇴",
  BUL: "🇧🇬",
  TUR: "🇹🇷",
  KAZ: "🇰🇿",
  CZE: "🇨🇿",
  SLO: "🇸🇮",
  CRO: "🇭🇷",
  GRE: "🇬🇷",
  ITA: "🇮🇹",
  SUI: "🇨🇭",
  SWE: "🇸🇪",
  DEN: "🇩🇰",
  FIN: "🇫🇮",
  BRA: "🇧🇷",
  ARG: "🇦🇷",
  CAN: "🇨🇦",
  MEX: "🇲🇽",
  CUB: "🇨🇺",
  ISR: "🇮🇱",
  IRI: "🇮🇷",
  VIE: "🇻🇳",
  INA: "🇮🇩",
  PHI: "🇵🇭",
  MGL: "🇲🇳",
};

function Flag({ code }) {
  return (
    <span
      className={FLAGS[code] ? "ol-flag" : "ol-flag-code"}
      title={code || ""}
    >
      {FLAGS[code] || code || "–"}
    </span>
  );
}

function calculateMatch(teamA, teamB, lineupA, lineupB, firstColour = "white", modelStrategy = CHESS_MODEL_STRATEGY, strengthReference) {
  if (!teamA || !teamB || lineupA.length !== 4 || lineupB.length !== 4)
    return null;
  if (modelStrategy === BETTY_MODEL_STRATEGY && !strengthReference) return null;
  const boards = lineupA.map((a, index) => {
    const teamAColor =
      (index % 2 === 0) === (firstColour === "white") ? "white" : "black";
    return {
      ...buildBoardProbabilities({
        teamAPlayer: asPlayer(a, `${teamA.id}-a-${index}`),
        teamBPlayer: asPlayer(lineupB[index], `${teamB.id}-b-${index}`),
        teamAColor,
        effectiveMinutes: OLYMPIAD_MODEL_MINUTES,
        parameters: DEFAULT_CHESS_MODEL_PARAMETERS,
        modelStrategy, strengthReference,
      }),
      teamAColor,
    };
  });
  return { boards, probabilities: calculateTeamMatchProbabilities(boards), modelStrategy, strengthReference };
}

function lineupFromStoredMatch(match, team) {
  const rosterById = new Map(
    (team?.roster || []).map((entry) => [entry.player_id, entry])
  );
  return Array.from({ length: 4 }, (_, index) => {
    const board = (match?.boards || [])[index];
    return (
      (board &&
        (rosterById.get(board.white_player_id) ||
          rosterById.get(board.black_player_id))) ||
      (team?.roster || [])[index] ||
      placeholderPlayer(team, index + 1)
    );
  });
}

function TeamPicker({ label, teams, value, onChange }) {
  return (
    <Autocomplete
      fullWidth
      options={teams}
      value={value}
      onChange={onChange}
      getOptionLabel={(team) =>
        `${team.initial_seed ? `${team.initial_seed}. ` : ""}${olympiadTeamDisplayName(team)} · ${team.federation || "–"}`
      }
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" InputLabelProps={{...params.InputLabelProps, shrink:true}} />
      )}
    />
  );
}

function Probability({ value, mode = "probabilities" }) {
  const values = [value.teamAWin, value.draw, value.teamBWin];
  const labels = formatProbabilityTriplet(values);
  return <div className="ol-probability" aria-label="Mannschaftswahrscheinlichkeiten 1 X 2">
    <div className="ol-prob-columns">{values.map((probability,index) => <div key={index}>
      <small>{["1 · links", "X · Remis", "2 · rechts"][index]}</small>
      <strong>{mode === "odds" ? odd(value.fairOdds[["teamAWin","draw","teamBWin"][index]]) : labels[index]}</strong>
    </div>)}</div>
    <div className="ol-prob-bar">{values.map((probability,index) => <i key={index} style={{width: `${probability*100}%`}} />)}</div>
  </div>;
}

function StrengthLabel({ player, reference }) {
  if (!reference) return null;
  const initialized = initializeTournamentPlayer(player?.rating_used, reference);
  const value = initialized.strength * 100;
  const label = value < 0.01 ? "<0,01" : value > 99.99 ? ">99,99" : value.toFixed(2).replace(".", ",");
  return <small className="ol-strength">betty {label} %{initialized.usesReplacement && <span> · {MISSING_ELO_INITIALIZATION_LABEL}</span>}</small>;
}

function BoardPlayer({ player, white, right = false, strengthReference }) {
  return <div className={`ol-player ${right ? "right" : ""}`}>
    <strong>{player?.name_used || "Nicht gemeldet"}</strong>
    <div className="ol-player-meta">
      {player?.title_used && <span className="ol-title-badge">{player.title_used}</span>}
      <b>{player?.rating_used ?? "–"}</b><small>Elo</small>
      <span className={`ol-color-piece ${white ? "white" : "black"}`} aria-label={white ? "Weiß" : "Schwarz"} />
      <span>{white ? "Weiß" : "Schwarz"}</span>
    </div>
    {chessPlayerBirthLabel(player) && <small>{chessPlayerBirthLabel(player)}</small>}
    <StrengthLabel player={player} reference={strengthReference} />
  </div>;
}

function Lineup({ team, value, onChange, strengthReference }) {
  const toggle = (entry) => onChange(selectOlympiadLineup(value, entry));
  return (
    <div className="ol-lineup">
      {(team?.roster || []).map((entry) => (
        <Button
          key={entry.player_id}
          size="small"
          variant={
            value.some((item) => item.player_id === entry.player_id)
              ? "contained"
              : "outlined"
          }
          onClick={() => toggle(entry)}
        >
          {entry.board_order}. {entry.name_used} · {entry.rating_used}
          <StrengthLabel player={entry} reference={strengthReference} />
        </Button>
      ))}
    </div>
  );
}

function Boards({ calculation, lineupA, lineupB, storedBoards = [], onResult, saving }) {
  return (
    <div className="ol-boards" data-testid="olympiad-board-rows">
      {calculation.boards.map((board, index) => {
        const aWhite = board.teamAColor === "white";
        const results = [
          [board.teamAWin, board.fairOdds.teamAWin],
          [board.draw, board.fairOdds.draw],
          [board.teamBWin, board.fairOdds.teamBWin],
        ];
        const labels = formatProbabilityTriplet(results.map(([probability]) => probability));
        return (
          <div className="ol-board" key={index}>
            <b>{index + 1}</b>
            <BoardPlayer player={lineupA[index]} white={aWhite} strengthReference={calculation.modelStrategy === BETTY_MODEL_STRATEGY ? calculation.strengthReference : null} />
            <div className="ol-result-entry">
              {onResult ? <select aria-label={`Ergebnis Brett ${index + 1}`} disabled={saving}
                value={boardResultInputValue(storedBoards[index]?.result, board.teamAColor)}
                onChange={event => onResult(index, whiteResultFromTeamAScore(event.target.value, board.teamAColor))}>
                <option value="">offen</option><option value="1">1 – 0</option>
                <option value="0.5">½ – ½</option><option value="0">0 – 1</option>
                <option value="+-">Kampflos: links gewinnt (+ : −)</option>
                <option value="-+">Kampflos: rechts gewinnt (− : +)</option>
                <option value="--">Kampflos: beide verlieren (− : −)</option>
              </select> : <strong>{boardResultLabel(storedBoards[index]?.result, board.teamAColor)}</strong>}
              <small title={storedBoards[index]?.updated_at || ""}>{storedBoards[index]?.result_source === "official" ? "offiziell" : storedBoards[index]?.result_source === "manual" ? "manuell" : "offen"}</small>
            </div>
            <BoardPlayer player={lineupB[index]} white={!aWhite} right strengthReference={calculation.modelStrategy === BETTY_MODEL_STRATEGY ? calculation.strengthReference : null} />
            <div className="ol-board-odds">
              {results.map(([, fair], i) => (
                <span key={i}>
                  <small>{["1 · links", "X · Remis", "2 · rechts"][i]}</small>
                  <strong>{labels[i]}</strong>
                  <small>Quote {odd(fair)}</small>
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RosterAverages({roster, count = roster?.length, compact = false}) {
  const stats = olympiadRosterStatistics(roster, count);
  const number = value => value == null ? "–" : value.toLocaleString("de-DE", {maximumFractionDigits:1});
  return <span className={`ol-averages ${compact ? "compact" : ""}`}>
    <span title={`Elo-Schnitt nur gerateter Spieler: ${stats.rated} von ${stats.count}`}><small>Ø Elo</small> <b>{number(stats.elo)}</b>{stats.rated !== stats.count && <em>({stats.rated}/{stats.count} geratet)</em>}</span>
    <span title="Jahrgangsalter: 2026 minus Geburtsjahr"><small>Ø Alter</small> <b>{number(stats.age)}</b>{stats.age == null && <em>({stats.knownAges}/{stats.count} Jahrgänge bekannt)</em>}</span>
  </span>;
}

const performanceNumber = value => value == null ? "–" : value.toLocaleString("de-DE", {minimumFractionDigits:2, maximumFractionDigits:2});
const performancePercent = value => value == null ? "–" : `${performanceNumber(100*value)} %`;
const chessBalance = value => value > 0 ? `+${value}` : value < 0 ? `−${Math.abs(value)}` : "0";
const signedPerformance = value => value == null ? "–" : `${value > 0 ? "+" : ""}${performanceNumber(value)}`;
function PerformanceSummary({title, stats}) {
  return <Paper className="ol-performance-summary" data-testid="olympiad-performance-summary">
    <h3>{title}</h3>
    <table><thead><tr><th>Partien / Ergebnisse</th><th>Erwartet</th><th>Eingetroffen</th></tr></thead><tbody>
      <tr><th>Anzahl Partien</th><td>{stats.games}</td><td>{stats.finished}</td></tr>
      {["1 · Weißsiege", "X · Remisen", "2 · Schwarzsiege"].map((label,i)=><tr key={label}><th>{label}</th><td>{performanceNumber(stats.expected[i])}</td><td>{stats.actual[i]}</td></tr>)}
      <tr className="ol-performance-average"><th>Ø Wahrscheinlichkeit</th><td>{performancePercent(stats.expectedAverage)}</td><td>{performancePercent(stats.actualAverage)}</td></tr>
    </tbody></table>
    <small>Beide Spalten: dieselben {stats.games} beendeten, tatsächlich gespielten Partien.</small>
    <details className="ol-performance-explanation">
      <summary>Warum wird so gerechnet?</summary>
      <p>Für jede ausgewertete Partie nehmen wir die Prognose vor dem Ergebnis: p₁ für Weißsieg, pₓ für Remis und p₂ für Schwarzsieg. Erwartete Ergebnisanzahlen entstehen durch Addition der jeweiligen Wahrscheinlichkeiten; eingetroffene Ergebnisse werden gezählt.</p>
      <p>Bei der durchschnittlichen Wahrscheinlichkeit bewerten wir, wie wahrscheinlich das tatsächlich eintretende Ergebnis laut Prognose war. Ein Ergebnis mit Wahrscheinlichkeit p tritt laut Modell mit Wahrscheinlichkeit p ein und liefert dann den Wert p. Sein erwarteter Beitrag ist deshalb p × p. Über alle drei möglichen Ergebnisse ergibt das p₁² + pₓ² + p₂².</p>
      <p>Beispiel: 60 % Weißsieg, 30 % Remis, 10 % Schwarzsieg. Erwartet: 0,6 × 0,6 + 0,3 × 0,3 + 0,1 × 0,1 = <b>46 %</b>. Tritt ein Remis ein, beträgt der eingetroffene Wert dieser Partie <b>30 %</b>; bei Weißsieg wären es 60 %, bei Schwarzsieg 10 %.</p>
      <p>Für N beendete, tatsächlich gespielte Partien mitteln wir: erwartet Σ(p₁² + pₓ² + p₂²) / N, eingetroffen Σ p(tatsächliches Ergebnis) / N. <b>Beide Werte verwenden exakt dieselben Partien und denselben Nenner.</b> Offene und kampflose Partien zählen in keiner Spalte. Ohne ausgewertete Partie gibt es noch keinen Durchschnitt.</p>
      <p>Das ist die vom Modell erwartete Wahrscheinlichkeit des späteren Ergebnisses, keine Quote richtig getippter Sieger. Abweichungen sind bei einzelnen Partien und kleinen Stichproben normal; der Vergleich allein beweist nicht die Qualität oder Kalibrierung eines Modells.</p>
    </details>
    <small>{stats.open} offen ausgeschlossen · {stats.forfeits} kampflos ausgeschlossen{stats.unavailable > 0 ? ` · ${stats.unavailable} beendete Partien ohne auswertbare Prognose ausgeschlossen` : ""}{stats.reconstructed > 0 ? ` · ${stats.reconstructed} Prognosen nachgerechnet` : ""}</small>
  </Paper>;
}
function PlayerRoundResult({entry}) {
  if (!entry) return null;
  return <span title={`${entry.colour} gegen ${entry.opponent || "unbekannt"}${entry.expected == null ? "" : ` · Erwartete Punkte ${performanceNumber(entry.expected)}`}${entry.forfeit ? " · kampflos, nicht bewertet" : ""}`}>
    {entry.forfeit ? (entry.score === 1 ? "+" : "−") : entry.score == null ? "…" : boardScoreLabel(entry.score)}
  </span>;
}

function MatchCard({
  teamA,
  teamB,
  lineupA,
  lineupB,
  calculation,
  status,
  round,
  boardNumber,
  mode,
  open,
  toggle,
  onEdit,
  score,
  match, onResult, saving,
}) {
  if (!teamA || !teamB || !calculation) return null;
  const prematch = calculation.modelStrategy === CHESS_MODEL_STRATEGY ? match?.prematch : null;
  const forecastBoards = prematch?.boards || calculation.boards;
  const before = prematch?.probabilities || calculateTeamMatchProbabilities(forecastBoards);
  const knownScores = calculation.boards.map((board, index) => boardScoresFromResult(match?.boards?.[index]?.result, board.teamAColor));
  const finished = knownScores.filter(value => value != null);
  let current = calculateTeamMatchProbabilities(forecastBoards, knownScores);
  if (match?.completed && finished.length !== 4) {
    const a = Number(match.team_a_score), b = Number(match.team_b_score);
    current = { teamAWin: a > b ? 1 : 0, draw: a === b ? 1 : 0, teamBWin: b > a ? 1 : 0,
      fairOdds: { teamAWin: a > b ? 1 : null, draw: a === b ? 1 : null, teamBWin: b > a ? 1 : null } };
  }
  const aScore = finished.reduce((sum,value) => sum+value[0],0);
  const scoreText = match?.completed && finished.length !== 4 ? score : finished.length ? `${boardScoreLabel(aScore)} : ${boardScoreLabel(finished.reduce((sum,value) => sum+value[1],0))}` : "– : –";
  return (
    <Paper className="ol-match-card" data-testid="olympiad-match-card">
      <div className="ol-match-meta">
        <strong>⊙ &nbsp;{status}</strong>
        <span>
          Runde {round} · Tisch {boardNumber || "–"}
          {onEdit && (
            <button className="ol-edit-match" onClick={onEdit}>
              Bearbeiten
            </button>
          )}
        </span>
      </div>
      <div className="ol-match-main">
        <div className="ol-team home">
          <span><strong><small className="ol-seed" title="Startnummer">{teamA.start_number || teamA.initial_seed}.</small> {olympiadTeamDisplayName(teamA)}</strong><RosterAverages roster={lineupA} count={4} compact /></span>
          <Flag code={teamA.federation} />
        </div>
        <div className="ol-live-score"><strong>{scoreText}</strong><small>{match?.completed || finished.length === 4 ? "Beendet" : `${4-finished.length} Bretter offen`}</small></div>
        <div className="ol-team">
          <Flag code={teamB.federation} />
          <span><strong><small className="ol-seed" title="Startnummer">{teamB.start_number || teamB.initial_seed}.</small> {olympiadTeamDisplayName(teamB)}</strong><RosterAverages roster={lineupB} count={4} compact /></span>
        </div>
      </div>
      <div className="ol-match-prognoses">
        <div><div className="ol-prognosis-heading"><b>Vor Beginn</b>{calculation.modelStrategy === BETTY_MODEL_STRATEGY && <small> · betty-Modellrechnung</small>}{calculation.modelStrategy === CHESS_MODEL_STRATEGY && match && !match.prematch && <small> · noch nicht gesichert</small>}{prematch?.basis === "reconstructed" && <small> · nachträglich rekonstruiert</small>}</div><Probability value={before} mode={mode} /></div>
        <div><div className="ol-prognosis-heading"><b>Aktuell</b></div><Probability value={current} mode={mode} /></div>
      </div>
      <button className="ol-expand" onClick={toggle} aria-expanded={open}>
        <span>Bretter, Wahrscheinlichkeiten und faire Quoten</span>
        <span>{open ? "⌃" : "⌄"}</span>
      </button>
      {open && (
        <Boards calculation={calculation} lineupA={lineupA} lineupB={lineupB} storedBoards={match?.boards} onResult={onResult} saving={saving} />
      )}
    </Paper>
  );
}

function ViewHeading({ title, subtitle, badge }) {
  return (
    <div className="ol-view-heading">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <span>{badge}</span>
    </div>
  );
}

export function ChessOlympiadWorkspace() {
  const [section, setSection] = useState("open"),
    [tab, setTab] = useState("matches"),
    [round, setRound] = useState(1);
  const [modelStrategy, setModelStrategy] = useState(CHESS_MODEL_STRATEGY);
  const [loadedReference, setLoadedReference] = useState(null);
  const strengthReference = loadedReference?.section === section ? loadedReference : null;
  const [teams, setTeams] = useState([]),
    [matches, setMatches] = useState([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState("");
  const [teamA, setTeamA] = useState(null),
    [teamB, setTeamB] = useState(null),
    [lineupA, setLineupA] = useState([]),
    [lineupB, setLineupB] = useState([]);
  const [firstColour, setFirstColour] = useState("white"),
    [displayMode, setDisplayMode] = useState("probabilities"),
    [showBoards, setShowBoards] = useState(false),
    [showComparison, setShowComparison] = useState(false),
    [openMatch, setOpenMatch] = useState(null);
  const [matchSearch, setMatchSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [matchSort, setMatchSort] = useState("official");
  const [probabilityFilter, setProbabilityFilter] = useState("all");
  const [tableNumber, setTableNumber] = useState(1);
  const [sourceStatus, setSourceStatus] = useState("manual");
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [refreshError, setRefreshError] = useState("");
  const [saveMessage, setSaveMessage] = useState(null);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [confirmCorrection, setConfirmCorrection] = useState(false);
  const [teamAScore, setTeamAScore] = useState("");
  const [teamBScore, setTeamBScore] = useState("");
  const [simulationType, setSimulationType] = useState("current"),
    [runs, setRuns] = useState(100),
    [simulationTeam, setSimulationTeam] = useState(null),
    [simulation, setSimulation] = useState(null),
    [simulationExample, setSimulationExample] = useState(null),
    [progress, setProgress] = useState(0),
    [simulating, setSimulating] = useState(false),
    [simulationRuns, setSimulationRuns] = useState(0),
    [simulationError, setSimulationError] = useState("");
  const cancel = useRef(false);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setSyncStatus(null);
    setRefreshError("");
    setError("");
    Promise.all([
      loadChessOlympiadField(section),
      listChessOlympiadMatches(section),
    ])
      .then(([field, real]) => {
        if (!active) return;
        const importedTeams = field.teams || [];
        setLoadedReference(createTournamentStrengthReference(section, importedTeams));
        const rankedTeams = importedTeams.filter(
          (team) => Number(team.start_number) > 0
        );
        const ordered = initialOlympiadRanking(rankedTeams.length ? rankedTeams : importedTeams);
        setTeams(ordered);
        const germany = ordered.find(team => team.federation === "GER") || ordered[0] || null;
        setTeamA(germany);
        setLineupA(defaultLineup(germany));
        setSimulationTeam(germany);
        setRound(Math.max(1, ...(real.matches || []).map(match => Number(match.round))));
        setMatches(real.matches || []);
        setSyncStatus(real.sync_status || null);
        setTeamB(null);
        setSimulation(null);
        setSimulationExample(null);
      })
      .catch((reason) => active && setError(reason.message))
      .finally(() => active && setBusy(false));
    return () => {
      active = false;
    };
  }, [section]);
  const chooseA = (_, team) => {
      setTeamA(team);
      setLineupA(defaultLineup(team));
    },
    chooseB = (_, team) => {
      setTeamB(team);
      setLineupB(defaultLineup(team));
    };
  const calculation = useMemo(
    () => calculateMatch(teamA, teamB, lineupA, lineupB, firstColour, modelStrategy, strengthReference),
    [teamA, teamB, lineupA, lineupB, firstColour, modelStrategy, strengthReference]
  );
  const completedRound = Math.max(
    0,
    ...matches
      .filter((item) => item.completed)
      .map((item) => Number(item.round))
  );
  const roundMatches = matches.filter((item) => Number(item.round) === round);
  const teamById = useMemo(
    () => new Map(teams.map((team) => [team.id, team])),
    [teams]
  );
  const performanceRecords = useMemo(() => strengthReference
    ? olympiadPerformanceRecords(matches, teams, modelStrategy, strengthReference) : [],
    [matches, teams, modelStrategy, strengthReference]);
  const roundPerformance = useMemo(() => summarizeOlympiadPerformance(performanceRecords.filter(item => item.round === round)), [performanceRecords, round]);
  const totalPerformance = useMemo(() => summarizeOlympiadPerformance(performanceRecords), [performanceRecords]);
  const teamPerformance = useMemo(() => olympiadPlayerPerformance(performanceRecords, (teamA?.roster || []).map(player => player.player_id)), [performanceRecords, teamA]);
  const playerPerformance = useMemo(() => new Map((teamA?.roster || []).map(player => [player.player_id, olympiadPlayerPerformance(performanceRecords, [player.player_id])])), [performanceRecords, teamA]);
  const matchAnalysis = (item) => {
    const a = teamById.get(item.team_a_id);
    const b = teamById.get(item.team_b_id);
    const la = item.boards?.length === 4 ? lineupFromStoredMatch(item, a) : defaultLineup(a);
    const lb = item.boards?.length === 4 ? lineupFromStoredMatch(item, b) : defaultLineup(b);
    const calculation = calculateMatch(a, b, la, lb, item.team_a_colour || "white", modelStrategy, strengthReference);
    return { a, b, la, lb, calculation };
  };
  const visibleRoundMatches = roundMatches.filter((item) => {
    const text = `${item.team_a_name || ""} ${item.team_b_name || ""} ${
      teamById.get(item.team_a_id)?.federation || ""
    } ${teamById.get(item.team_b_id)?.federation || ""}`.toLowerCase();
    const status =
      item.source_status ||
      (item.completed
        ? "played"
        : item.lineup_status === "fixed"
        ? "manual"
        : "assumed");
    const { calculation } = matchAnalysis(item);
    const favourite = calculation
      ? Math.max(calculation.probabilities.teamAWin, calculation.probabilities.teamBWin)
      : 0;
    const probabilityMatches =
      probabilityFilter === "all" ||
      (probabilityFilter === "balanced" && favourite < 0.6) ||
      (probabilityFilter === "clear" && favourite >= 0.6 && favourite < 0.8) ||
      (probabilityFilter === "very-clear" && favourite >= 0.8);
    return (
      (!matchSearch || text.includes(matchSearch.toLowerCase())) &&
      (statusFilter === "all" || status === statusFilter) &&
      probabilityMatches
    );
  }).sort((left, right) => {
    if (matchSort === "official") return Number(left.match_number) - Number(right.match_number);
    const a = matchAnalysis(left).calculation?.probabilities;
    const b = matchAnalysis(right).calculation?.probabilities;
    if (!a || !b) return 0;
    const favouriteA = Math.max(a.teamAWin, a.teamBWin);
    const favouriteB = Math.max(b.teamAWin, b.teamBWin);
    if (matchSort === "tight") return favouriteA - favouriteB;
    if (matchSort === "underdog") {
      return Math.min(b.teamAWin, b.teamBWin) - Math.min(a.teamAWin, a.teamBWin);
    }
    return favouriteB - favouriteA;
  });
  const standings = useMemo(() => {
    const state = new Map(
      teams.map((team) => [
        team.id,
        { ...team, matchPoints: 0, boardPoints: 0, opponentBoardPoints: 0, openBoards: 0 },
      ])
    );
    matches
      .forEach((match) => {
        const a = state.get(match.team_a_id),
          b = state.get(match.team_b_id);
        if (!a || !b) return;
        const live = olympiadLiveMatchScore(match);
        if (!live.hasResults) return;
        const [x,y] = live.scores;
        a.openBoards += live.openBoards;
        b.openBoards += live.openBoards;
        a.boardPoints += x;
        b.boardPoints += y;
        a.opponentBoardPoints += y;
        b.opponentBoardPoints += x;
        if (live.completed) {
          a.matchPoints += x > y ? 2 : x === y ? 1 : 0;
          b.matchPoints += y > x ? 2 : x === y ? 1 : 0;
        }
      });
    return olympiadStandings([...state.values()]);
  }, [teams, matches]);
  const standingsPerformance = useMemo(() => {
    return new Map(teams.map(team => [team.id, olympiadPlayerPerformance(performanceRecords, team.roster.map(player => player.player_id))]));
  }, [teams, performanceRecords]);
  const liveTableRound = Math.max(0, ...matches.filter(match => olympiadLiveMatchScore(match).hasResults).map(match => Number(match.round)));
  const roundInsights = useMemo(() => {
    const analysed = roundMatches
      .map((match) => {
        const teamA = teamById.get(match.team_a_id);
        const teamB = teamById.get(match.team_b_id);
        const lineupA =
          match.boards?.length === 4
            ? lineupFromStoredMatch(match, teamA)
            : defaultLineup(teamA);
        const lineupB =
          match.boards?.length === 4
            ? lineupFromStoredMatch(match, teamB)
            : defaultLineup(teamB);
        const result = calculateMatch(
          teamA,
          teamB,
          lineupA,
          lineupB,
          match.team_a_colour || "white", modelStrategy, strengthReference
        );
        return result
          ? { match, teamA, teamB, probabilities: (modelStrategy === CHESS_MODEL_STRATEGY && match.prematch?.probabilities) || result.probabilities,
              current: calculateTeamMatchProbabilities((modelStrategy === CHESS_MODEL_STRATEGY && match.prematch?.boards) || result.boards,
                result.boards.map((board,index) => boardScoresFromResult(match.boards?.[index]?.result, board.teamAColor))) }
          : null;
      })
      .filter(Boolean);
    if (!analysed.length) return null;
    const favourite = [...analysed].sort(
      (a, b) =>
        Math.max(b.probabilities.teamAWin, b.probabilities.teamBWin) -
        Math.max(a.probabilities.teamAWin, a.probabilities.teamBWin)
    )[0];
    const tightest = [...analysed].sort(
      (a, b) =>
        Math.abs(a.probabilities.teamAWin - a.probabilities.teamBWin) -
        Math.abs(b.probabilities.teamAWin - b.probabilities.teamBWin)
    )[0];
    const upset = [...analysed]
      .filter(({ match }) => match.completed)
      .map((item) => {
        const aWon =
          Number(item.match.team_a_score) > Number(item.match.team_b_score);
        const bWon =
          Number(item.match.team_b_score) > Number(item.match.team_a_score);
        return {
          ...item,
          winnerProbability: aWon
            ? item.probabilities.teamAWin
            : bWon
            ? item.probabilities.teamBWin
            : item.probabilities.draw,
          winner: aWon ? item.teamA : bWon ? item.teamB : { name: `${item.teamA.name} – ${item.teamB.name} (Remis)` },
        };
      })
      .filter((item) => item.winner && item.winnerProbability < Math.max(item.probabilities.teamAWin, item.probabilities.draw, item.probabilities.teamBWin))
      .sort((a, b) => a.winnerProbability - b.winnerProbability)[0];
    const favouriteTeam =
      favourite.probabilities.teamAWin >= favourite.probabilities.teamBWin
        ? favourite.teamA
        : favourite.teamB;
    const favouriteProbability = Math.max(
      favourite.probabilities.teamAWin,
      favourite.probabilities.teamBWin
    );
    const possibleUpset = [...analysed].filter(item => !item.match.completed &&
      (item.probabilities.teamAWin <= item.probabilities.teamBWin ? item.current.teamAWin : item.current.teamBWin) > 0).sort(
      (a, b) =>
        Math.min(a.probabilities.teamAWin, a.probabilities.teamBWin) -
        Math.min(b.probabilities.teamAWin, b.probabilities.teamBWin)
    )[0];
    const underdog =
      possibleUpset?.probabilities.teamAWin <=
      possibleUpset?.probabilities.teamBWin
        ? possibleUpset?.teamA
        : possibleUpset?.teamB;
    return {
      favourite: `${olympiadTeamDisplayName(favouriteTeam)} · ${pct0(
        favouriteProbability
      )}`,
      tightest: `${olympiadTeamDisplayName(
        tightest.teamA
      )} – ${olympiadTeamDisplayName(tightest.teamB)}`,
      possibleUpset: underdog ? olympiadTeamDisplayName(underdog) : "Keine mehr möglich",
      finished: roundMatches.filter(match => match.completed).length,
      running: roundMatches.filter(match => !match.completed && match.boards?.some(board => board.result)).length,
      dropped: analysed.filter(item => item.match.completed && (item.probabilities.teamAWin >= item.probabilities.teamBWin
        ? Number(item.match.team_a_score) <= Number(item.match.team_b_score)
        : Number(item.match.team_b_score) <= Number(item.match.team_a_score))).map(item => olympiadTeamDisplayName(item.probabilities.teamAWin >= item.probabilities.teamBWin ? item.teamA : item.teamB)).join(", "),
      actualUpset: upset
        ? `${olympiadTeamDisplayName(upset.winner)} · ${pct0(
            upset.winnerProbability
          )}`
        : null,
    };
  }, [roundMatches, teamById, modelStrategy, strengthReference]);
  const runSimulation = async () => {
    if (simulating) return;
    cancel.current = false;
    setSimulation(null);
    setSimulationExample(null);
    setSimulationError("");
    setProgress(0);
    setSimulating(true);
    const total = Math.min(1000, Math.max(1, Number(runs)));
    const stats = new Map(
      teams.map((team) => [
        team.id,
        {
          wins: 0,
          top3: 0,
          top10: 0,
          place: 0,
          places: Array(teams.length).fill(0),
        },
      ])
    );
    const baseline =
      matches.filter((item) => item.completed);
    const finalRound =
      simulationType === "next" ? Math.min(ROUNDS, completedRound + 1) : ROUNDS;
    let completed = 0;
    let example = null;
    try {
      // Let React paint the running state before the first expensive tournament.
      await new Promise((resolve) => setTimeout(resolve, 0));
      while (completed < total && !cancel.current) {
        const result = simulateOlympiadTournament({
          teams,
          rounds: finalRound,
          effectiveMinutes: OLYMPIAD_MODEL_MINUTES,
          parameters: DEFAULT_CHESS_MODEL_PARAMETERS,
          modelStrategy, strengthReference,
          baseline,
          officialPairings: matches,
          captureRounds: completed === 0,
          shouldCancel: () => cancel.current,
        });
        if (!result || cancel.current) break;
        const table = Array.isArray(result) ? result : result.table;
        if (!example && !Array.isArray(result)) example = result;
        table.forEach((team, place) => {
          const item = stats.get(team.id);
          item.place += place + 1;
          item.places[place] += 1;
          if (!place) item.wins += 1;
          if (place < 3) item.top3 += 1;
          if (place < 10) item.top10 += 1;
        });
        completed += 1;
        setProgress((completed / total) * 100);
        // One full tournament per batch keeps paint and cancel responsive.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      if (!cancel.current && completed === total) {
        setSimulationRuns(completed);
        setSimulationExample(example);
        setSimulation(
          [...stats.entries()]
            .map(([id, item]) => ({
              id,
              ...item,
              expectedPlace: item.place / completed,
            }))
            .sort((a, b) => b.wins - a.wins || a.expectedPlace - b.expectedPlace)
        );
      }
    } catch (reason) {
      if (!cancel.current)
        setSimulationError(
          reason?.message || "Die Simulation konnte nicht abgeschlossen werden."
        );
    } finally {
      setProgress(0);
      setSimulating(false);
      cancel.current = false;
    }
  };
  const selected =
    simulation?.find((item) => item.id === simulationTeam?.id) || simulation?.[0];
  const selectedSimulationTeam = selected ? teamById.get(selected.id) : null;
  const mostFrequentPlace = selected
    ? selected.places.indexOf(Math.max(...selected.places)) + 1
    : null;
  const exampleRound = simulationExample?.rounds?.[0] || null;
  const visibleSimulationMatches = exampleRound
    ? exampleRound.matches.filter(
        (match) =>
          !simulationTeam ||
          match.teamA.id === simulationTeam.id ||
          match.teamB.id === simulationTeam.id
      )
    : [];
  const simulationUpset = exampleRound
    ? [...exampleRound.matches]
        .map((match) => {
          const aWon = match.scoreA > match.scoreB;
          const bWon = match.scoreB > match.scoreA;
          return {
            ...match,
            winner: aWon ? match.teamA : bWon ? match.teamB : null,
            winnerProbability: aWon
              ? match.probabilities.teamAWin
              : bWon
              ? match.probabilities.teamBWin
              : null,
          };
        })
        .filter(({ winner }) => winner)
        .sort((a, b) => a.winnerProbability - b.winnerProbability)[0]
    : null;
  const favouriteDroppedPoints = exampleRound
    ? [...exampleRound.matches]
        .filter((match) => {
          const favouriteA = match.probabilities.teamAWin >= match.probabilities.teamBWin;
          return favouriteA ? match.scoreA <= match.scoreB : match.scoreB <= match.scoreA;
        })
        .sort(
          (a, b) =>
            Math.max(b.probabilities.teamAWin, b.probabilities.teamBWin) -
            Math.max(a.probabilities.teamAWin, a.probabilities.teamBWin)
        )[0]
    : null;
  const saveBoardResult = async (match, index, result) => {
    if (match.boards?.length !== 4) {
      setError("Bitte zuerst die tatsächliche Aufstellung über Bearbeiten festlegen.");
      return;
    }
    if (match.boards[index].result_source === "official" && match.boards[index].result !== result
        && !window.confirm("Offizielles Ergebnis durch einen manuellen Wert ersetzen?")) return;
    setSaving(true); setError("");
    try {
      await saveChessOlympiadMatch(section, { ...match, expected_updated_at: match.updated_at,
        boards: match.boards.map((board, boardIndex) => boardIndex === index ? { ...board, result, result_source: "manual" } : board) });
      const refreshed = await listChessOlympiadMatches(section);
      setMatches(refreshed.matches || []);
    } catch (reason) { setError(reason.message); }
    finally { setSaving(false); }
  };
  const [importPreview, setImportPreview] = useState(null);
  useEffect(() => {
    if (busy || saving || editingMatchId || simulating || importPreview) return;
    let active = true, pending = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden" || pending) return;
      pending = true;
      try {
        const real = await listChessOlympiadMatches(section, false);
        if (!active) return;
        setMatches(real.matches || []);
        setSyncStatus(real.sync_status || null);
        setRefreshError("");
      } catch (reason) {
        if (active) setRefreshError(`Ansicht konnte nicht aktualisiert werden: ${reason.message}`);
      } finally { pending = false; }
    };
    const timer = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [section, busy, saving, editingMatchId, simulating, importPreview]);

  const previewResults = async (candidate) => {
    setSaving(true); setError("");
    try { setImportPreview(await previewChessOlympiadResults(section, round, candidate)); }
    catch (reason) { setError(reason.message); }
    finally { setSaving(false); }
  };
  const applyResults = async () => {
    setSaving(true); setError("");
    try {
      await applyChessOlympiadResults(section, importPreview);
      const refreshed = await listChessOlympiadMatches(section);
      setMatches(refreshed.matches || []); setImportPreview(null);
    } catch (reason) { setError(reason.message); }
    finally { setSaving(false); }
  };
  const saveRealMatch = async () => {
    if (!calculation || lineupA.length !== 4 || lineupB.length !== 4) {
      setSaveMessage({
        severity: "error",
        text: "Bitte zwei Mannschaften und jeweils vier Spieler auswählen.",
      });
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const boards = lineupA.map((playerA, index) => {
        const aWhite = (index % 2 === 0) === (firstColour === "white");
        return {
          team_a_player_id: playerA.player_id,
          team_b_player_id: lineupB[index].player_id,
          white_player_id: aWhite
            ? playerA.player_id
            : lineupB[index].player_id,
          black_player_id: aWhite
            ? lineupB[index].player_id
            : playerA.player_id,
        };
      });
      await saveChessOlympiadMatch(section, {
        id: editingMatchId,
        round,
        match_number: Number(tableNumber),
        team_a_id: teamA.id,
        team_b_id: teamB.id,
        team_a_colour: firstColour,
        source_status: sourceStatus,
        team_a_score:
          sourceStatus === "played" && teamAScore !== ""
            ? Number(teamAScore)
            : null,
        team_b_score:
          sourceStatus === "played" && teamBScore !== ""
            ? Number(teamBScore)
            : null,
        confirm_official_correction: confirmCorrection,
        correction_source: confirmCorrection
          ? "admin_confirmed_source_correction"
          : null,
        boards,
      });
      const refreshed = await listChessOlympiadMatches(section);
      setMatches(refreshed.matches || []);
      setSaveMessage({
        severity: "success",
        text: "Die reale Paarung wurde gespeichert und in die Rundenübersicht übernommen.",
      });
      setEditingMatchId(null);
      setConfirmCorrection(false);
    } catch (reason) {
      setSaveMessage({
        severity: "error",
        text: reason.message || "Die Paarung konnte nicht gespeichert werden.",
      });
    } finally {
      setSaving(false);
    }
  };
  const editRealMatch = (match) => {
    const a = teamById.get(match.team_a_id),
      b = teamById.get(match.team_b_id);
    const locked = Boolean(
      match.locked ||
        match.source_status === "official" ||
        match.source_status === "played"
    );
    if (
      locked &&
      !window.confirm(
        "Offizielle oder gespielte Daten sind gesperrt. Quellenfehler ausdrücklich zur Korrektur öffnen?"
      )
    )
      return;
    setTeamA(a);
    setTeamB(b);
    setLineupA(lineupFromStoredMatch(match, a));
    setLineupB(lineupFromStoredMatch(match, b));
    setRound(Number(match.round));
    setTableNumber(Number(match.match_number));
    setFirstColour(match.team_a_colour || "white");
    setSourceStatus(match.source_status || "manual");
    setTeamAScore(match.team_a_score ?? "");
    setTeamBScore(match.team_b_score ?? "");
    setEditingMatchId(match.id);
    setConfirmCorrection(locked);
    setShowComparison(true);
  };
  return (
    <main className="ol-workspace" data-testid="chess-olympiad-workspace">
      <header className="ol-header">
        <div>
          <small>BETTY · SCHACH INTERN</small>
          <h1>46. Schacholympiade</h1>
          <p>Samarkand · 16.–27. September 2026</p>
        </div>
        <div>
          <button
            className={section === "open" ? "active" : ""}
            disabled={simulating} onClick={() => setSection("open")}
          >
            OPEN
          </button>
          <button
            className={section === "women" ? "active" : ""}
            disabled={simulating} onClick={() => setSection("women")}
          >
            FRAUEN
          </button>
        </div>
      </header>
      <div className="ol-model-selector">
        <ToggleButtonGroup exclusive size="small" value={modelStrategy} disabled={simulating || busy}
          aria-label="Prognosemodell" onChange={(_, value) => {
            if (!value) return;
            setModelStrategy(value); setSimulation(null); setSimulationExample(null); setProgress(0);
          }}>
          <ToggleButton value={CHESS_MODEL_STRATEGY}>Original-Elo</ToggleButton>
          <ToggleButton value={BETTY_MODEL_STRATEGY} disabled={!strengthReference}>betty-Spielstärke</ToggleButton>
        </ToggleButtonGroup>
        {!busy && !strengthReference && <small>betty-Spielstärke nicht verfügbar: kein gültiger Elo-Referenzwert für diese Sektion.</small>}
        {modelStrategy === BETTY_MODEL_STRATEGY && strengthReference && <div data-testid="betty-reference">
          <strong>{section === "open" ? "Open" : "Frauen"} · turnierbezogene Initialisierung · R₀ = {strengthReference.referenceElo.toLocaleString("de-DE", { maximumFractionDigits: 6 })} Elo</strong>
          <p>Die betty-Spielstärken wurden für diese Sektion vorläufig am Elo-Durchschnitt des Teilnehmerfeldes normiert. 50 % entsprechen der erwarteten Punktausbeute gegen diesen Referenzspieler. Die Werte sind noch keine weltweite Normierung. Open und Frauen haben getrennte Anker; ihre absoluten Spielstärkewerte sind nicht miteinander vergleichbar.</p>
          <small>{strengthReference.ratedPlayerCount} Spieler mit gültiger Elo · Ersatzwert: {strengthReference.replacementElo.toLocaleString("de-DE", { maximumFractionDigits: 6 })} Elo aus den {strengthReference.replacementPlayerCount} niedrigsten gültigen Elo-Werten · 150 Modellminuten</small>
        </div>}
      </div>
      <nav className="ol-round-nav">
        <div>
          <button onClick={() => setRound(Math.max(1, round - 1))}>‹</button>
          <strong>Runde {round}</strong>
          <button onClick={() => setRound(Math.min(ROUNDS, round + 1))}>
            ›
          </button>
        </div>
        <div>
          <span>{teams.length} Teams</span>
          <span>11 Runden</span>
          <span>4 Bretter</span>
        </div>
      </nav>
      <Tabs
        className="ol-tabs"
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="fullWidth"
      >
        <Tab value="matches" label="Mannschaftskämpfe" />
        <Tab value="table" label="Tabelle" />
        <Tab value="teams" label="Mannschaften" />
        <Tab value="simulation" label="Simulation" />
      </Tabs>
      {busy && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!busy && teams.length > 0 && <section className="ol-performance" aria-label="Runden- und Gesamtstatistik">
        <PerformanceSummary title={`Runde ${round}`} stats={roundPerformance} />
        <PerformanceSummary title="Gesamt · alle geladenen Runden" stats={totalPerformance} />
        <p>Einzelpartien · {modelStrategy === BETTY_MODEL_STRATEGY ? "betty-Spielstärke" : "Original-Elo"} · Prognose vor Ergebnis, gespeicherte oder nachgerechnete Werte. Nur veröffentlichte Spieleraufstellungen; kampflose Partien ausgeschlossen. Erwartung und eingetroffen: ausschließlich dieselben beendeten, tatsächlich gespielten Partien.</p>
      </section>}
      {tab === "matches" && (
        <section className="ol-view" data-testid="olympiad-matches-view">
          <ViewHeading
            title="Mannschaftskämpfe"
            subtitle="Auslosung, Aufstellungen und betty-Wahrscheinlichkeiten"
            badge={`${roundMatches.length} offizielle Kämpfe · ${Math.max(
              0,
              teams.length - roundMatches.length * 2
            )} Mannschaften derzeit ohne Paarung`}
          />
          <div className="ol-result-import">
            <div role="status" data-testid="olympiad-auto-sync-status">
              Automatischer Ergebnisabgleich alle 2 Minuten · Chess-Results + Lichess · Ansicht alle 30 Sekunden
              {syncStatus?.round ? ` · Runde ${syncStatus.round}` : ""}
              {syncStatus?.last_success
                ? ` · Zuletzt geprüft: ${new Date(syncStatus.last_success).toLocaleString("de-DE")}`
                : " · Erster Abgleich steht aus"}
              {(saving || editingMatchId || simulating || importPreview) && " · Ansicht während Bearbeitung pausiert"}
            </div>
            {syncStatus?.error && <Alert severity="warning">Offizieller Abgleich fehlgeschlagen: {syncStatus.error} Automatischer neuer Versuch folgt.</Alert>}
            {syncStatus?.warning && <Alert severity="warning">{syncStatus.warning}</Alert>}
            {refreshError && <Alert severity="warning">{refreshError} Automatischer neuer Versuch folgt.</Alert>}

            <Button disabled={saving} onClick={() => previewResults()}>Offizielle Ergebnisse prüfen</Button>
            <small>Modellzeit: {OLYMPIAD_MODEL_MINUTES} Minuten · 60-Züge-Regel (90 + 30 + 30)</small>
            {importPreview && <div>
              <strong>Importvorschau: {importPreview.matches.length} Kämpfe · {importPreview.counts.conflicts} offene Konflikte</strong>
              <div className="ol-import-changes">{importPreview.matches.map(item => <div key={item.id}>
                <b>Tisch {item.match_number} · {teamById.get(item.team_a_id)?.name} – {teamById.get(item.team_b_id)?.name}</b>
                <span> {item.boards.map((board,index) => `B${index+1}: ${board.result || "offen"}`).join(" · ")} (Weißsicht)</span>
              </div>)}</div>
              {importPreview.errors.map((item,index) => <Alert severity="error" key={index}>{item.reason}</Alert>)}
              {(importPreview.conflicts || []).map(conflict => <div key={`${conflict.match_id}-${conflict.board}`} className="ol-import-conflict">
                <span>{matches.find(item => item.id === conflict.match_id)?.team_a_name || conflict.match_id} · Brett {conflict.board}: manuell {conflict.manual} / offiziell {conflict.official} (Weißsicht)</span>
                <select aria-label={`Konflikt Brett ${conflict.board}`} value={conflict.decision || ""} disabled={saving}
                  onChange={event => previewResults({ ...importPreview, matches: importPreview.matches.map(item => item.id === conflict.match_id ? { ...item, result_decisions: { ...item.result_decisions, [conflict.board]: event.target.value } } : item) })}>
                  <option value="">Bewusst entscheiden …</option><option value="keep_manual">Manuell behalten</option><option value="use_official">Offiziell übernehmen</option>
                </select>
              </div>)}
              <Button disabled={saving || !!importPreview.errors.length || !!importPreview.counts.conflicts} onClick={applyResults}>Geprüfte Ergebnisse übernehmen</Button>
              <Button onClick={() => setImportPreview(null)}>Verwerfen</Button>
            </div>}
          </div>
          <div className="ol-display-toggle"><Button variant={displayMode === "probabilities" ? "contained" : "outlined"} onClick={()=>setDisplayMode("probabilities")}>Wahrscheinlichkeiten</Button><Button variant={displayMode === "odds" ? "contained" : "outlined"} onClick={()=>setDisplayMode("odds")}>Faire Quoten</Button></div>
          <div className="ol-match-filters">
            <TextField
              size="small"
              label="Mannschaft oder Föderation"
              value={matchSearch}
              onChange={(event) => setMatchSearch(event.target.value)}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="assumed">Angenommen</MenuItem>
              <MenuItem value="manual">Manuell gespeichert</MenuItem>
              <MenuItem value="official">Offiziell</MenuItem>
              <MenuItem value="played">Beendet</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Sortierung"
              value={matchSort}
              onChange={(event) => setMatchSort(event.target.value)}
            >
              <MenuItem value="official">Offizielle Tischreihenfolge</MenuItem>
              <MenuItem value="tight">Knappste Kämpfe</MenuItem>
              <MenuItem value="favourite">Größte Favoriten</MenuItem>
              <MenuItem value="underdog">Größte Außenseiterchancen</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Wahrscheinlichkeitsbereich"
              value={probabilityFilter}
              onChange={(event) => setProbabilityFilter(event.target.value)}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="balanced">Favorit unter 60 %</MenuItem>
              <MenuItem value="clear">Favorit 60–80 %</MenuItem>
              <MenuItem value="very-clear">Favorit ab 80 %</MenuItem>
            </TextField>
          </div>
          <div className="ol-match-layout">
            <div className="ol-match-list">
              {!roundMatches.length && (
                <div className="ol-empty">
                  ⓘ{" "}
                  <span>
                    Für Runde {round} sind bei uns noch keine offiziellen Paarungen geladen. Bitte den offiziellen Abgleich prüfen.
                  </span>
                </div>
              )}
              {visibleRoundMatches.map((match) => {
                const { a, b, la, lb, calculation: matchCalculation } = matchAnalysis(match);
                return (
                  <MatchCard
                    key={match.id}
                    match={match}
                    onResult={(index, result) => saveBoardResult(match, index, result)}
                    saving={saving}
                    teamA={a}
                    teamB={b}
                    lineupA={la}
                    lineupB={lb}
                    calculation={matchCalculation}
                    status={
                      match.source_status === "official"
                        ? match.boards?.length === 4
                          ? "Offizielle Paarung und Aufstellung"
                          : "Offizielle Paarung · Aufstellung noch nicht veröffentlicht · Spieler 1–4 angenommen"
                        : match.source_status === "played" || match.completed
                        ? "Tatsächlich gespielt"
                        : match.source_status === "manual" ||
                          match.lineup_status === "fixed"
                        ? "Manuell gespeichert"
                        : "Aufstellung angenommen"
                    }
                    round={match.round}
                    boardNumber={match.match_number}
                    mode={displayMode}
                    open={openMatch === match.id}
                    toggle={() =>
                      setOpenMatch(openMatch === match.id ? null : match.id)
                    }
                    onEdit={() => editRealMatch(match)}
                    score={
                      match.completed
                        ? `${Number(match.team_a_score).toFixed(1)} : ${Number(
                            match.team_b_score
                          ).toFixed(1)}`
                        : null
                    }
                  />
                );
              })}
              <Paper className="ol-manual">
                <button onClick={() => setShowComparison(!showComparison)}>
                  <span>
                    <strong>Eigener Mannschaftsvergleich</strong>
                    <small>
                      Nachrangige Analyse mit frei gewählten Teams und
                      Aufstellungen
                    </small>
                  </span>
                  <span>{showComparison ? "⌃" : "⌄"}</span>
                </button>
                {showComparison && (
                  <div className="ol-manual-body">
                    <Stack direction={{ xs: "column", md: "row" }} gap={1}>
                      <TeamPicker
                        label="Mannschaft A suchen"
                        teams={teams}
                        value={teamA}
                        onChange={chooseA}
                      />
                      <TeamPicker
                        label="Mannschaft B suchen"
                        teams={teams}
                        value={teamB}
                        onChange={chooseB}
                      />
                    </Stack>
                    {teamA && teamB && (
                      <>
                        <div className="ol-lineups">
                          <Lineup
                            strengthReference={modelStrategy === BETTY_MODEL_STRATEGY ? strengthReference : null}
                            team={teamA}
                            value={lineupA}
                            onChange={setLineupA}
                          />
                          <Lineup
                            strengthReference={modelStrategy === BETTY_MODEL_STRATEGY ? strengthReference : null}
                            team={teamB}
                            value={lineupB}
                            onChange={setLineupB}
                          />
                        </div>
                        <Stack direction="row" gap={1} flexWrap="wrap" my={1}>
                          <Button
                            onClick={() => {
                              setLineupA(defaultLineup(teamA));
                              setLineupB(defaultLineup(teamB));
                            }}
                          >
                            Aufstellung zurücksetzen
                          </Button>
                          <TextField
                            select
                            size="small"
                            label="Brett 1"
                            value={firstColour}
                            onChange={(event) =>
                              setFirstColour(event.target.value)
                            }
                          >
                            <MenuItem value="white">A Weiß</MenuItem>
                            <MenuItem value="black">A Schwarz</MenuItem>
                          </TextField>
                          {sourceStatus === "played" && (
                            <>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, max: 4, step: 0.5 }}
                                label="Punkte Mannschaft A"
                                value={teamAScore}
                                onChange={(event) =>
                                  setTeamAScore(event.target.value)
                                }
                              />
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, max: 4, step: 0.5 }}
                                label="Punkte Mannschaft B"
                                value={teamBScore}
                                onChange={(event) =>
                                  setTeamBScore(event.target.value)
                                }
                              />
                            </>
                          )}
                          <TextField
                            size="small"
                            type="number"
                            label="Tisch"
                            value={tableNumber}
                            onChange={(event) =>
                              setTableNumber(
                                Math.max(1, Number(event.target.value))
                              )
                            }
                          />
                          <TextField
                            select
                            size="small"
                            label="Aufstellungsstatus"
                            value={sourceStatus}
                            onChange={(event) =>
                              setSourceStatus(event.target.value)
                            }
                          >
                            <MenuItem value="assumed">
                              Angenommen / eigene Variante
                            </MenuItem>
                            <MenuItem value="manual">
                              Manuell gespeichert
                            </MenuItem>
                            <MenuItem value="played">
                              Tatsächlich gespielt
                            </MenuItem>
                          </TextField>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={displayMode}
                            onChange={(_, value) =>
                              value && setDisplayMode(value)
                            }
                          >
                            <ToggleButton value="probabilities">
                              Wahrscheinlichkeiten
                            </ToggleButton>
                            <ToggleButton value="odds">
                              Faire Quoten
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                        <MatchCard
                          teamA={teamA}
                          teamB={teamB}
                          lineupA={lineupA}
                          lineupB={lineupB}
                          calculation={calculation}
                          status="Eigener Vergleich"
                          round={round}
                          mode={displayMode}
                          open={showBoards}
                          toggle={() => setShowBoards(!showBoards)}
                        />
                        {saveMessage && (
                          <Alert severity={saveMessage.severity}>
                            {saveMessage.text}
                          </Alert>
                        )}
                        <Button
                          variant="contained"
                          disabled={saving || !calculation}
                          onClick={saveRealMatch}
                        >
                          {saving
                            ? "Wird gespeichert …"
                            : "Als reale Paarung speichern"}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </Paper>
            </div>
            <Paper className="ol-round-summary">
              <small>Rundenstand</small><strong>{roundInsights?.finished || 0} beendet · {roundInsights?.running || 0} laufend</strong>
              <small>Favoriten mit Punktverlust</small><strong>{roundInsights?.dropped || "Bisher keine"}</strong>
              <h3>Runde {round}</h3>
              <span>Favorit der Runde</span>
              <strong>
                {roundInsights?.favourite || "Nach Auslosung berechenbar"}
              </strong>
              <span>Engster Kampf</span>
              <strong>
                {roundInsights?.tightest || "Noch nicht verfügbar"}
              </strong>
              <span>Größte mögliche Überraschung</span>
              <strong>
                {roundInsights?.possibleUpset || "Nach Auslosung berechenbar"}
              </strong>
              <span>Größte tatsächliche Überraschung</span>
              <strong>
                {roundInsights?.actualUpset || "Bisher keine Überraschung"}
              </strong>
            </Paper>
          </div>
        </section>
      )}
      {tab === "table" && (
        <section className="ol-view" data-testid="olympiad-table-view">
          <ViewHeading
            title="Tabelle"
            subtitle={
              liveTableRound
                ? `Live-Stand bis Runde ${liveTableRound} · beendete Bretter sofort berücksichtigt`
                : "Noch keine reale Tabelle"
            }
            badge={`${teams.length} Mannschaften`}
          />
          {!liveTableRound ? (
            <Paper className="ol-table-empty">
              Noch keine beendeten Bretter oder abgeschlossenen Mannschaftskämpfe. Ergebnisse erscheinen hier automatisch nach dem Abgleich.
            </Paper>
          ) : (
            <Paper className="ol-table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Platz</TableCell>
                    <TableCell>Mannschaft</TableCell>
                    <TableCell align="center">Mannschaftspunkte</TableCell>
                    <TableCell align="center">Brettpunkte</TableCell>
                    <TableCell align="center" title="Feinwertung zur Reihenfolge bei gleichen Mannschaftspunkten. Nur vorhandene Werte werden angezeigt.">Tie-Break</TableCell>
                    <TableCell align="center">Ø Elo · Ø Alter</TableCell>
                    <TableCell align="center" title="Siege minus Niederlagen in tatsächlich gespielten Einzelpartien; ohne kampflose Partien.">Bilanz S−N</TableCell>
                    <TableCell align="right" title="Eigene Modellbilanz der Spieler, ohne kampflose Partien. Original-Elo: K = 10 für alle.">{modelStrategy === BETTY_MODEL_STRATEGY ? "Δ Prozentpunkte" : "Δ Elo"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {standings.map((team, index) => (
                    <TableRow key={team.id}>
                      <TableCell>
                        <strong>{index + 1}</strong>
                      </TableCell>
                      <TableCell>
                        <span className="ol-table-team">
                          <Flag code={team.federation} />
                          <span>
                            <strong>{olympiadTeamDisplayName(team)}</strong>
                            <small>{team.federation}</small>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <strong>{team.matchPoints}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <span className="ol-board-points-pair">{boardScoreLabel(team.boardPoints)}:{boardScoreLabel(team.opponentBoardPoints)}</span>
                        {team.openBoards > 0 && <small className="ol-table-live">{team.openBoards} Bretter offen</small>}
                      </TableCell>
                      <TableCell align="center">
                        {Number.isFinite(Number(team.tieBreak))
                          ? Number(team.tieBreak).toFixed(1)
                          : "–"}
                      </TableCell>
                      <TableCell align="center">
                        <RosterAverages roster={team.roster} />
                      </TableCell>
                      <TableCell align="center"><strong>{chessBalance(standingsPerformance.get(team.id)?.balance || 0)}</strong></TableCell>
                      <TableCell align="right"><strong>{signedPerformance(modelStrategy === BETTY_MODEL_STRATEGY ? standingsPerformance.get(team.id)?.percentagePointDelta : standingsPerformance.get(team.id)?.eloDelta)}</strong></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          {liveTableRound > 0 && (
            <p className="ol-table-note">
              Live-Tabelle: Brettpunkte aus allen bereits beendeten Brettern, einschließlich offizieller kampfloser Punkte. Mannschaftspunkte erst nach Abschluss des gesamten Kampfes. Bilanz S−N und Modellbilanz berücksichtigen sofort jede beendete, tatsächlich gespielte Einzelpartie, auch in laufenden Kämpfen. Die Reihenfolge ist während der Runde vorläufig. Tie-Break: Feinwertung bei gleichen Mannschaftspunkten. Reihenfolge: Mannschaftspunkte, vorhandener Tie-Break,
              Brettpunkte, danach deterministisch nach Startsetzung. Nicht
              verfügbare offizielle Tie-Break-Daten werden nicht erfunden.
            </p>
          )}
        </section>
      )}
      {tab === "teams" && (
        <section className="ol-view" data-testid="olympiad-teams-view">
          <ViewHeading
            title="Mannschaften"
            subtitle="Meldelisten, Brettreihenfolge und Wertungszahlen"
            badge={`${teams.length} Teams`}
          />
          <div className="ol-teams-layout">
            <Paper className="ol-team-picker">
              <TeamPicker
                label="Mannschaft oder Föderation"
                teams={teams}
                value={teamA}
                onChange={chooseA}
              />
              <div>
                {teams.map((team, index) => (
                  <button
                    key={team.id}
                    className={teamA?.id === team.id ? "active" : ""}
                    onClick={() => chooseA(null, team)}
                  >
                    <b className="ol-team-number">{index + 1}</b><Flag code={team.federation} />
                    <span>
                      <strong>{olympiadTeamDisplayName(team)}</strong>
                      <small>
                        {team.federation} <RosterAverages roster={team.roster} compact />
                      </small>
                    </span>
                  </button>
                ))}
              </div>
            </Paper>
            <Paper className="ol-team-detail">
              {teamA ? (
                <>
                  <div className="ol-team-title">
                    <Flag code={teamA.federation} />
                    <div>
                      <h3>{teamA.initial_seed}. {olympiadTeamDisplayName(teamA)}</h3>
                      <RosterAverages roster={teamA.roster} />
                      <p>
                        {teamA.federation} · {teamA.roster.length} gemeldete
                        Spieler
                      </p>
                    </div>
                  </div>
                  <div className="ol-team-performance" data-testid="olympiad-team-performance">
                    <strong>{teamPerformance.games} bewertete Partien</strong>
                    <span>Ergebnis: <b>{boardScoreLabel(teamPerformance.playedScore)}/{teamPerformance.playedGames}</b> · Erwartung: <b>{performanceNumber(teamPerformance.expected)}</b></span>
                    <strong>{modelStrategy === BETTY_MODEL_STRATEGY ? `Abweichung ${signedPerformance(teamPerformance.percentagePointDelta)} Prozentpunkte` : `Team-Elo-Bilanz ${signedPerformance(teamPerformance.eloDelta)}`}</strong>
                  </div>
                  <p className="ol-performance-note">{modelStrategy === BETTY_MODEL_STRATEGY
                    ? "Ergebnisabweichung = 100 × (erzielte − erwartete Punkte) / bewertete Partien, in Prozentpunkten. Keine Fortschreibung der betty-Spielstärke."
                    : "Eigene Elo-Modellbilanz: K = 10 für alle, ΔElo = 10 × (erzielte − erwartete Punkte). Keine offizielle FIDE-Auswertung; Meldelisten-Elo bleibt unverändert."} + = kampfloser Sieg, − = kampflose Niederlage; beide zählen weder zu Punkten noch Partien der Ergebnissumme. Leer = kein Einsatz/keine Aufstellung, … = Partie offen.</p>
                  <div className="ol-roster-results-scroll"><Table size="small" className="ol-roster-table ol-roster-results">
                    <TableHead>
                      <TableRow>
                        <TableCell>Meldeplatz</TableCell>
                        <TableCell>Titel</TableCell>
                        <TableCell>Spieler</TableCell>
                        {Array.from({length:ROUNDS},(_,i)=><TableCell key={i} align="center">R{i+1}</TableCell>)}
                        <TableCell align="right">Elo</TableCell>
                        <TableCell align="right">{modelStrategy === BETTY_MODEL_STRATEGY ? "Δ Prozentpunkte" : "Δ Elo"}</TableCell>
                        <TableCell align="right">Ergebnis</TableCell>
                        <TableCell align="right" title="Siege minus Niederlagen, ohne kampflose Partien.">Bilanz S−N</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamA.roster.map((entry) => (
                        <TableRow key={entry.player_id}>
                          <TableCell>{entry.board_order}</TableCell>
                          <TableCell>{entry.title_used || "–"}</TableCell>
                          <TableCell>
                            <strong>{entry.name_used}</strong>
                            {chessPlayerBirthLabel(entry) && <div><small>{chessPlayerBirthLabel(entry)}</small></div>}
                            {modelStrategy === BETTY_MODEL_STRATEGY && <div className="ol-player-points"><small>Punkte {boardScoreLabel(playerPerformance.get(entry.player_id)?.playedScore)} · erwartet {performanceNumber(playerPerformance.get(entry.player_id)?.expected)}</small></div>}
                          </TableCell>
                          {Array.from({length:ROUNDS},(_,i)=><TableCell key={i} align="center"><PlayerRoundResult entry={playerPerformance.get(entry.player_id)?.rounds[i+1]?.[0]} /></TableCell>)}
                          <TableCell align="right">
                            <strong>{entry.rating_used ?? "–"}</strong>
                            {modelStrategy === BETTY_MODEL_STRATEGY && <StrengthLabel player={entry} reference={strengthReference} />}
                          </TableCell>
                          <TableCell align="right" title={`Punkte ${performanceNumber(playerPerformance.get(entry.player_id)?.score)} · Erwartung ${performanceNumber(playerPerformance.get(entry.player_id)?.expected)} · ${playerPerformance.get(entry.player_id)?.games || 0} bewertete Partien`}><strong>{signedPerformance(modelStrategy === BETTY_MODEL_STRATEGY ? playerPerformance.get(entry.player_id)?.percentagePointDelta : playerPerformance.get(entry.player_id)?.eloDelta)}</strong></TableCell>
                          <TableCell align="right" className="ol-player-result-total" title="Punkte / beendete, tatsächlich gespielte Partien; ohne kampflose Ergebnisse"><strong>{boardScoreLabel(playerPerformance.get(entry.player_id)?.playedScore)}/{playerPerformance.get(entry.player_id)?.playedGames || 0}</strong></TableCell>
                          <TableCell align="right"><strong>{chessBalance(playerPerformance.get(entry.player_id)?.balance || 0)}</strong></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></div>
                </>
              ) : (
                <div className="ol-detail-empty">
                  Mannschaft auswählen, um die vollständige Meldeliste zu sehen.
                </div>
              )}
            </Paper>
          </div>
        </section>
      )}
      {tab === "simulation" && (
        <section className="ol-view" data-testid="olympiad-simulation">
          <ViewHeading
            title="Simulation"
            subtitle="Eigene Prognose – echte Ergebnisse bleiben unverändert"
            badge="Monte Carlo"
          />
          <div className="ol-sim-layout">
            <Paper className="ol-sim-controls">
              <h3>Turnier simulieren</h3>
              <p>
                Die Simulation arbeitet ausschließlich mit einer eigenen Kopie
                des Turnierstands.
              </p>
              <TextField
                select
                fullWidth
                size="small"
                label="Ausgangspunkt"
                value={simulationType}
                onChange={(e) => setSimulationType(e.target.value)}
              >
                {!completedRound && <MenuItem value="full">Ab Runde 1</MenuItem>}
                <MenuItem value="current">Ab aktuellem Stand</MenuItem>
                <MenuItem value="next">Nur nächste Runde</MenuItem>
              </TextField>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Simulationen"
                value={runs}
                inputProps={{ min: 1, max: 1000, step: 100 }}
                onChange={(e) =>
                  setRuns(Math.min(1000, Math.max(1, Number(e.target.value))))
                }
              />
              <TeamPicker
                label="Mannschaft analysieren"
                teams={teams}
                value={simulationTeam}
                onChange={(_, value) => setSimulationTeam(value)}
              />
              <Button
                variant="contained"
                disabled={simulating}
                onClick={runSimulation}
              >
                Neu simulieren
              </Button>
              {simulating && (
                <Button
                  onClick={() => {
                    cancel.current = true;
                  }}
                >
                  Abbrechen
                </Button>
              )}
              {simulating && (
                <>
                  <LinearProgress variant="determinate" value={progress} />
                  <small>Simulation läuft · {progress.toFixed(0)} %</small>
                </>
              )}
              {simulationError && (
                <Alert severity="error">{simulationError}</Alert>
              )}
            </Paper>
            <Paper className="ol-sim-result">
              {simulation ? (
                <>
                  <h3>Simulationsergebnis</h3>
                  <div className="ol-sim-favourite">
                    <small>Häufigster Turniersieger</small>
                    <strong>
                      {olympiadTeamDisplayName(teamById.get(simulation[0].id))}
                    </strong>
                    <b>{displayMode === "odds" ? odd(simulation[0].wins / simulationRuns) : pct(simulation[0].wins / simulationRuns)}</b>
                  </div>
                  <p className="ol-sim-origin">
                    {simulationRuns} Simulationen · Ausgangspunkt: {simulationType === "full"
                      ? "offizielle Paarungen der Runde 1"
                      : simulationType === "current"
                      ? "aktueller realer Stand"
                      : "kommende Runde"}. Nicht veröffentlichte Paarungen: Schweizer Näherung.
                  </p>
                  <div className="ol-display-toggle"><Button variant={displayMode === "probabilities" ? "contained" : "outlined"} onClick={()=>setDisplayMode("probabilities")}>Wahrscheinlichkeiten</Button><Button variant={displayMode === "odds" ? "contained" : "outlined"} onClick={()=>setDisplayMode("odds")}>Faire Quoten</Button></div>
                  <div className="ol-sim-scroll" role="region" aria-label="Alle Mannschaften der Simulation" tabIndex={0}><Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nr.</TableCell><TableCell>Mannschaft</TableCell>
                        <TableCell>Gold</TableCell>
                        <TableCell>Top 3</TableCell>
                        <TableCell>Top 10</TableCell>
                        <TableCell>Erwarteter Platz</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {simulation.map((item, index) => (
                        <TableRow
                          key={item.id}
                          hover
                          selected={selected?.id === item.id}
                          onClick={() => setSimulationTeam(teamById.get(item.id))}
                          onDoubleClick={() => setSimulationTeam(teamById.get(item.id))}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{index + 1}</TableCell><TableCell>
                            <strong>
                              {olympiadTeamDisplayName(teamById.get(item.id))}
                            </strong>
                          </TableCell>
                          <TableCell>
                            {displayMode === "odds" ? odd(item.wins / simulationRuns) : pct(item.wins / simulationRuns)}
                          </TableCell>
                          <TableCell>
                            {displayMode === "odds" ? odd(item.top3 / simulationRuns) : pct(item.top3 / simulationRuns)}
                          </TableCell>
                          <TableCell>
                            {displayMode === "odds" ? odd(item.top10 / simulationRuns) : pct(item.top10 / simulationRuns)}
                          </TableCell>
                          <TableCell>{item.expectedPlace.toFixed(1)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></div>
                  {displayMode === "probabilities" && <div className="ol-sim-sums"><b>Summe aller {simulation.length} Teams</b><span>Gold {pct(simulation.reduce((sum,item)=>sum+item.wins,0)/simulationRuns)}</span><span>Top 3 {pct(simulation.reduce((sum,item)=>sum+item.top3,0)/simulationRuns)}</span><span>Top 10 {pct(simulation.reduce((sum,item)=>sum+item.top10,0)/simulationRuns)}</span></div>}
                  {selected && (
                    <div className="ol-placement-detail">
                      <strong>{olympiadTeamDisplayName(selectedSimulationTeam)}</strong>
                      <span>
                        Gold {displayMode === "odds" ? odd(selected.wins / simulationRuns) : pct(selected.wins / simulationRuns)} · Top 3{" "}
                        {displayMode === "odds" ? odd(selected.top3 / simulationRuns) : pct(selected.top3 / simulationRuns)} · Top 10{" "}
                        {displayMode === "odds" ? odd(selected.top10 / simulationRuns) : pct(selected.top10 / simulationRuns)} · durchschnittlicher
                        Platz {selected.expectedPlace.toFixed(1)} · häufigster Platz {mostFrequentPlace}
                      </span>
                      <div className="ol-place-histogram">
                        {placementBins(selected.places).map(({count,label}, index) => (
                          <i
                            key={index}
                            title={`Plätze ${label}: ${pct(count / simulationRuns)}`}
                            style={{
                              height: `${Math.max(
                                2,
                                (count / simulationRuns) * 100
                              )}%`,
                            }}
                          >
                            <small>{label}</small>
                          </i>
                        ))}
                      </div>
                      {simulationExample && (
                        <div className="ol-example-path">
                          <b>Beispielhafter Turnierverlauf</b>
                          {simulationExample.rounds.map((simRound) => {
                            const match = simRound.matches.find(
                              (item) =>
                                item.teamA.id === selected.id || item.teamB.id === selected.id
                            );
                            if (!match) return null;
                            const selectedIsA = match.teamA.id === selected.id;
                            return (
                              <span key={simRound.round}>
                                R{simRound.round} · {olympiadTeamDisplayName(
                                  selectedIsA ? match.teamB : match.teamA
                                )} · {selectedIsA
                                  ? `${match.scoreA.toFixed(1)}:${match.scoreB.toFixed(1)}`
                                  : `${match.scoreB.toFixed(1)}:${match.scoreA.toFixed(1)}`}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {exampleRound && (
                    <div className="ol-next-round">
                      <h3>
                        Simulierte Runde {exampleRound.round}
                      </h3>
                      <p>
                        Simulation – keine realen Ergebnisse · {exampleRound.source === "official"
                          ? "Offizielle Paarungen, simulierte Ergebnisse"
                          : "Simulierte Paarungen"}
                      </p>
                      <div className="ol-sim-insights">
                        <span>Größte simulierte Überraschung</span>
                        <strong>
                          {simulationUpset
                            ? `${olympiadTeamDisplayName(simulationUpset.winner)} · ${pct0(simulationUpset.winnerProbability)}`
                            : "Keine in diesem Durchlauf"}
                        </strong>
                        <span>Favorit mit Punktverlust</span>
                        <strong>
                          {favouriteDroppedPoints
                            ? `${olympiadTeamDisplayName(
                                favouriteDroppedPoints.probabilities.teamAWin >=
                                  favouriteDroppedPoints.probabilities.teamBWin
                                  ? favouriteDroppedPoints.teamA
                                  : favouriteDroppedPoints.teamB
                              )} · Tisch ${favouriteDroppedPoints.table}`
                            : "Keiner in diesem Durchlauf"}
                        </strong>
                      </div>
                      {visibleSimulationMatches.map((match) => (
                        <details key={match.id}>
                          <summary>
                          <span>Tisch {match.table}</span>
                          <strong>
                            {olympiadTeamDisplayName(match.teamA)}
                          </strong>
                          <b>{match.scoreA.toFixed(1)} : {match.scoreB.toFixed(1)}</b>
                          <strong>
                            {olympiadTeamDisplayName(match.teamB)}
                          </strong>
                          </summary>
                          {match.boardResults.map((board) => (
                            <div className="ol-sim-board" key={board.board}>
                              <span>Brett {board.board}</span>
                              <span>{olympiadRosterPlayerLabel(board.teamAPlayer)}<StrengthLabel player={board.teamAPlayer} reference={modelStrategy === BETTY_MODEL_STRATEGY ? strengthReference : null} /></span>
                              <b>{board.officialResult ? boardResultLabel(board.officialResult, board.teamAColor) : `${boardScoreLabel(board.scoreA)}–${boardScoreLabel(board.scoreB ?? 1-board.scoreA)}`}</b>
                              <span>{olympiadRosterPlayerLabel(board.teamBPlayer)}<StrengthLabel player={board.teamBPlayer} reference={modelStrategy === BETTY_MODEL_STRATEGY ? strengthReference : null} /></span>
                            </div>
                          ))}
                        </details>
                      ))}
                      <details className="ol-sim-table">
                        <summary>Tabelle nach dieser simulierten Runde</summary>
                        {exampleRound.table.slice(0, 20).map((team, index) => (
                          <div key={team.id}>
                            <span>{index + 1}.</span>
                            <strong>{olympiadTeamDisplayName(team)}</strong>
                            <span>{team.matchPoints} MP · <span className="ol-board-points-pair">{boardScoreLabel(team.boardPoints)}:{boardScoreLabel(team.opponentBoardPoints)}</span>
                        {team.openBoards > 0 && <small className="ol-table-live">{team.openBoards} Bretter offen</small>} BP</span>
                          </div>
                        ))}
                      </details>
                    </div>
                  )}
                </>
              ) : (
                <div className="ol-detail-empty">
                  Starte eine Simulation, um Turniersieger-, Top-3- und
                  Top-10-Wahrscheinlichkeiten zu sehen.
                </div>
              )}
            </Paper>
          </div>
        </section>
      )}
    </main>
  );
}
